<script setup lang="ts">
/*
 * One counterparty (client / vendor) row in the scope switcher + its OS_PROJECT
 * children nested under a dotted tree connector, with a per-counterparty
 * "Show more (N)" toggle past `max` projects.
 *
 * Extracted so the identical block can render under BOTH the tenant employer and
 * any subsidiary company that owns counterparties (placement is driven by the
 * counterparty's `ParentId` in `deriveScopeListGroups`) without duplicating the
 * markup. The component is pure chrome: it emits `select` / `toggle` and the
 * parent owns selection + expand state.
 *
 * Test hooks are unchanged from the inline version: `org-chip-option-<id>` on
 * every row and `org-chip-show-more-<counterpartyId>` on the projects toggle.
 */
import { computed } from 'vue';
import { MenuItem } from '@headlessui/vue';
import { Briefcase, Check, Folder } from 'lucide-vue-next';
import { useI18n } from 'vue-i18n';
import { employerFullName, employerShortBadge, type Employer } from '@/api/services/employers';
import type { CounterpartyEntry } from '@/composables/useScopeTree';
import './org-chip-tree.css';

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    entry: CounterpartyEntry;
    /** The selected employer id, or null in global scope (nothing highlighted). */
    activeId: string | null;
    /** Reactive set of expanded parent ids (shared with the parent). */
    expanded: Set<string>;
    /** Children past this count collapse behind "Show more (N)". */
    max?: number;
  }>(),
  { max: 5 },
);

const emit = defineEmits<{
  (e: 'select', employer: Employer): void;
  (e: 'toggle', key: string): void;
}>();

const key = computed(() => props.entry.employer.id);
const isExpanded = computed(() => props.expanded.has(key.value));
const projects = computed(() => props.entry.projects);
const visibleProjects = computed(() =>
  projects.value.length <= props.max || isExpanded.value
    ? projects.value
    : projects.value.slice(0, props.max),
);
const hiddenCount = computed(() =>
  projects.value.length <= props.max || isExpanded.value ? 0 : projects.value.length - props.max,
);
const showToggle = computed(() => hiddenCount.value > 0 || isExpanded.value);

function isActive(id: string): boolean {
  return props.activeId !== null && props.activeId === id;
}
function fullName(e: Employer): string {
  return employerFullName(e);
}
function shortBadge(e: Employer): string {
  return employerShortBadge(e);
}
</script>

<template>
  <MenuItem v-slot="{ active }">
    <button
      type="button"
      :class="[
        'oc-child-row',
        isActive(entry.employer.id) ? 'bg-primary-50' : active ? 'bg-slate-50' : '',
      ]"
      :data-testid="`org-chip-option-${entry.employer.id}`"
      @click="emit('select', entry.employer)"
    >
      <span class="oc-icon oc-icon--sm" aria-hidden="true">
        <Briefcase class="h-4 w-4" />
      </span>
      <span class="flex min-w-0 flex-1 flex-col leading-tight">
        <span
          :class="[
            'truncate text-sm font-medium',
            isActive(entry.employer.id) ? 'text-primary-700' : 'text-app-fg',
          ]"
          :title="entry.employer.name"
        >
          {{ fullName(entry.employer) }}
        </span>
        <span
          v-if="shortBadge(entry.employer)"
          class="truncate text-[11px] font-normal text-app-muted"
        >
          {{ shortBadge(entry.employer) }}
        </span>
      </span>
      <Check
        v-if="isActive(entry.employer.id)"
        class="ml-auto h-4 w-4 shrink-0 text-primary"
        aria-hidden="true"
      />
    </button>
  </MenuItem>
  <div class="oc-children">
    <MenuItem v-for="project in visibleProjects" :key="project.id" v-slot="{ active }">
      <button
        type="button"
        :class="[
          'oc-child-row',
          isActive(project.id) ? 'bg-primary-50' : active ? 'bg-slate-50' : '',
        ]"
        :data-testid="`org-chip-option-${project.id}`"
        @click="emit('select', project)"
      >
        <span class="oc-icon oc-icon--sm" aria-hidden="true">
          <Folder class="h-4 w-4" />
        </span>
        <span class="flex min-w-0 flex-1 flex-col leading-tight">
          <span
            :class="[
              'truncate text-sm font-medium',
              isActive(project.id) ? 'text-primary-700' : 'text-app-fg',
            ]"
            :title="project.name"
          >
            {{ fullName(project) }}
          </span>
          <span v-if="shortBadge(project)" class="truncate text-[11px] font-normal text-app-muted">
            {{ shortBadge(project) }}
          </span>
        </span>
        <Check
          v-if="isActive(project.id)"
          class="ml-auto h-4 w-4 shrink-0 text-primary"
          aria-hidden="true"
        />
      </button>
    </MenuItem>
    <button
      v-if="showToggle"
      type="button"
      class="oc-show-more oc-show-more--child"
      :data-testid="`org-chip-show-more-${entry.employer.id}`"
      @click="emit('toggle', entry.employer.id)"
    >
      {{
        isExpanded
          ? t('v2.shell.org-chip.show-less')
          : t('v2.shell.org-chip.show-more', { count: hiddenCount })
      }}
    </button>
  </div>
</template>
